import './update-toast.css';
import { trackProps } from '../helpers/utils/analytics';
import { reportProblem } from '../helpers/utils/errorReporting';
import { onUpdateWaiting } from '../helpers/utils/updateOnReturn';
import { useScreenLayout } from '../helpers/utils/useScreenLayout';
import { useEffect, useState, type ReactNode } from 'react';
import { Box } from './ui/box';
import { Button } from './ui/button';
import { HStack, VStack } from './ui/stack';
import { Text } from './ui/text';
import { TOAST_SURFACE, ToastIconBadge } from './ui/toastSurface';
import { X } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/** How long the notice takes to slide out (update-toast.css). */
const EXIT_MS = 200;

/**
 * Fixed, centred at the bottom. It slides in and out with CSS, and stays
 * mounted while it slides out: `children` is the notice that was showing.
 */
function ToastFrame({
  show,
  wide,
  children,
}: {
  show: boolean;
  wide: boolean;
  children: ReactNode;
}) {
  const [mounted, setMounted] = useState(show);
  if (show && !mounted) setMounted(true);

  useEffect(() => {
    if (show || !mounted) return;
    const timer = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [show, mounted]);

  if (!mounted) return null;
  return (
    <div
      data-testid="update-toast"
      data-state={show ? 'open' : 'closed'}
      className="update-toast"
      // The keyframes repeat translateX(-50%), so the notice stays centred
      // while it moves.
      style={{
        position: 'fixed',
        bottom: wide ? '24px' : 'var(--spades-spacing-md)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
      }}
    >
      {children}
    </div>
  );
}

const OFFLINE_TOAST_AUTO_DISMISS_MS = 4000;

// Emerald/Toast insets that have no spacing token (14 left/bottom, 2 between
// title and description, 10 beside the action row).
const TOAST_INSET = '14px';
const TOAST_TEXT_GAP = '2px';
const TOAST_ACTION_INSET = '10px';
// Right inset that seats the 44pt X; the same on every notice.
const TOAST_CLOSE_INSET = '6px';

const UpdateNotification = () => {
  const wide = useScreenLayout() !== 'phone';
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [workerNeedsRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error: Error) {
      console.error('SW registration error', error);
      reportProblem('sw_register_failed', undefined, error);
    },
  });

  const [activatedUpdate, setActivatedUpdate] = useState(false);
  const needRefresh = workerNeedsRefresh || activatedUpdate;

  // A newer worker took over while the player was using the app: offer it
  // now. updateOnReturn.ts applies it on their next return otherwise, and
  // reloads straight into one that arrives before they touch anything.
  useEffect(() => onUpdateWaiting(() => setActivatedUpdate(true)), []);

  useEffect(() => {
    if (!offlineReady || needRefresh) return;
    const timer = setTimeout(
      () => setOfflineReady(false),
      OFFLINE_TOAST_AUTO_DISMISS_MS,
    );
    return () => clearTimeout(timer);
  }, [offlineReady, needRefresh, setOfflineReady]);

  const handleUpdate = () => {
    if (activatedUpdate) window.location.reload();
    else updateServiceWorker(true);
  };

  const handleDismiss = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
    setActivatedUpdate(false);
  };

  const current = needRefresh ? 'update' : offlineReady ? 'offline' : null;
  // The notice keeps its content while it slides out.
  const [shown, setShown] = useState(current);
  if (current !== null && current !== shown) setShown(current);
  const isUpdate = shown === 'update';

  return (
    <ToastFrame show={current !== null} wide={wide}>
      {shown && (
          <Box
            {...TOAST_SURFACE}
            zoom={wide ? 1.2 : 1}
            pl={TOAST_INSET}
            pr={TOAST_CLOSE_INSET}
            pt={isUpdate ? '8px' : '14px'}
            pb={TOAST_INSET}
          >
            <VStack gap="var(--spades-spacing-ms)" align="stretch">
              <HStack
                gap="var(--spades-spacing-ms)"
                align="flex-start"
                pt={isUpdate ? '6px' : 0}
              >
                <ToastIconBadge success={!isUpdate} />
                <VStack gap={TOAST_TEXT_GAP} align="start" flex="1" minW="1px">
                  <Text
                    fontWeight="700"
                    fontSize="var(--spades-font-size-sm)"
                    lineHeight={1.25}
                  >
                    {isUpdate ? 'New version available' : 'Works offline'}
                  </Text>
                  <Text
                    fontSize="var(--spades-font-size-xs)"
                    fontWeight="400"
                    lineHeight={1.25}
                    color="var(--spades-muted)"
                  >
                    {isUpdate
                      ? 'A new version of Spades Calculator is ready. Update now to get the latest features and improvements.'
                      : "Keep score even without internet. You can also install it for offline use from your browser's menu."}
                  </Text>
                </VStack>
                <Button
                  variant="ghost"
                  // The offline notice is not the update offer, so its X only
                  // dismisses; `update_dismissed` stays on the update card.
                  {...(isUpdate
                    ? trackProps('update_dismissed', { method: 'close' })
                    : {})}
                  onClick={handleDismiss}
                  aria-label="Dismiss"
                  w="44px"
                  h="44px"
                  minW="44px"
                  minH="44px"
                  p={0}
                  flexShrink={0}
                >
                  <X size={20} />
                </Button>
              </HStack>

              {isUpdate && (
                <HStack gap="var(--spades-spacing-sm)" pr={TOAST_ACTION_INSET}>
                  <Button
                    variant="primary"
                    h="44px"
                    minH="44px"
                    {...trackProps('update_requested')}
                    onClick={handleUpdate}
                    flex="1"
                  >
                    Update now
                  </Button>
                  <Button
                    variant="ghost"
                    h="44px"
                    minH="44px"
                    w="96px"
                    {...trackProps('update_dismissed', { method: 'later' })}
                    onClick={handleDismiss}
                  >
                    Later
                  </Button>
                </HStack>
              )}
            </VStack>
          </Box>
      )}
    </ToastFrame>
  );
};

export default UpdateNotification;
